import { useNavigate, useParams } from "react-router-dom";
import { Users, Presentation as PresentationIcon, ClipboardList, MessageCircleHeart } from "lucide-react";
import ClientShell from "./ClientShell";
import { JourneyRail } from "./JourneyRail";
import { useOurWork } from "@/lib/teamWorkQueries";

/**
 * Master v5 Prompt 22 — Team/Corporate "Our Work": everything authorized
 * and SHARED across the whole team_corporate Workspace. Every section
 * here is sourced server-side from `owner_member_id is None` rows only
 * (or an explicit team-approved share) — never any one member's private
 * attempts/cues, regardless of who is viewing.
 */
export default function OurWorkPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { data, isError } = useOurWork(workspaceId);

  if (isError) return <ClientShell><p className="text-ink-soft" data-testid="our-work-error">This Workspace's shared work isn't available.</p></ClientShell>;
  if (!data) {
    return (
      <ClientShell>
        <p className="text-ink-soft" data-testid="our-work-loading">Loading Our Work…</p>
      </ClientShell>
    );
  }

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="our-work-page">
        <div className="flex items-center gap-2 text-terracotta mb-1">
          <Users size={18} />
          <span className="text-xs uppercase tracking-wide font-medium">Shared with the team</span>
        </div>
        <h1 className="font-serif text-3xl text-ink mb-8">Our Work</h1>

        <div className="border border-ink/15 bg-white/40 rounded-xl p-6 mb-6">
          <p className="text-xs uppercase tracking-wide text-terracotta font-medium mb-4">Shared journey</p>
          <JourneyRail
            nodes={data.journey_rail}
            onNodeClick={null}
            emptyLabel="No shared journey stages yet."
            testIdPrefix="our-work-journey-rail"
          />
        </div>

        {data.shared_presentation_enabled && (
          <div className="border border-ink/15 bg-white/50 rounded-lg p-5 mb-6" data-testid="our-work-shared-presentation">
            <div className="flex items-center gap-2 text-ink-soft mb-2"><PresentationIcon size={16} /><span className="text-sm uppercase tracking-wide">Shared Presentation</span></div>
            {data.shared_presentation ? (
              <button
                onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/presentation/${data.shared_presentation.presentation_id}`)}
                className="text-sm font-medium text-terracotta hover:text-terracotta/80"
                data-testid="our-work-open-shared-presentation"
              >
                Open "{data.shared_presentation.title}"
              </button>
            ) : (
              <p className="text-sm text-ink-soft">No shared Presentation yet.</p>
            )}
          </div>
        )}

        <div className="border border-ink/15 bg-white/50 rounded-lg p-5 mb-6" data-testid="our-work-shared-assignments">
          <div className="flex items-center gap-2 text-ink-soft mb-3"><ClipboardList size={16} /><span className="text-sm uppercase tracking-wide">Shared assignments</span></div>
          {data.shared_assignments.length === 0 ? (
            <p className="text-sm text-ink-soft">No shared assignments yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.shared_assignments.map((a) => (
                <li key={a.asg_id} className="text-sm text-ink" data-testid={`our-work-assignment-${a.asg_id}`}>{a.title_snapshot}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-ink/15 bg-white/50 rounded-lg p-5" data-testid="our-work-shared-reflections">
          <div className="flex items-center gap-2 text-ink-soft mb-3"><MessageCircleHeart size={16} /><span className="text-sm uppercase tracking-wide">Shared reflections</span></div>
          {data.shared_reflections.length === 0 ? (
            <p className="text-sm text-ink-soft">No reflections have been shared with the team yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.shared_reflections.map((rf) => (
                <li key={rf.rfl_id} className="text-sm text-ink border-l-2 border-terracotta/40 pl-3" data-testid={`our-work-reflection-${rf.rfl_id}`}>{rf.response_text}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ClientShell>
  );
}
